import { Component, useEffect, useState, type ReactNode } from "react";
import { Button, Spinner } from "@discordforge/ui";
import { appData } from "./api";
import type { LoginData } from "./types";
import { AdminApp } from "./admin/AdminApp";
import { LoginScreen } from "./components/LoginScreen";
import { MailApp } from "./MailApp";

type Phase = "booting" | "login" | "mail";

/* Catches render errors so a bad message shape can never blank the screen. */
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
    state: { error: Error | null } = { error: null };

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    render() {
        if (this.state.error) {
            return (
                <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0b0b0f] p-6 text-center text-white">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-red-500/20 bg-red-500/10 text-2xl">
                        ⚠
                    </div>
                    <div>
                        <h1 className="text-lg font-black tracking-tight">Something went wrong</h1>
                        <p className="mt-1 max-w-md text-sm text-zinc-400">
                            {String(this.state.error.message || this.state.error)}
                        </p>
                    </div>
                    <Button variant="secondary" onClick={() => this.setState({ error: null })}>
                        Try again
                    </Button>
                </div>
            );
        }
        return this.props.children;
    }
}

export function App() {
    return (
        <ErrorBoundary>
            <AppInner />
        </ErrorBoundary>
    );
}

function AppInner() {
    const [route, setRoute] = useState<"mail" | "admin">(
        window.location.hash.startsWith("#/admin") ? "admin" : "mail"
    );

    // `#/admin` renders the admin panel (separate session from mailboxes).
    useEffect(() => {
        const onHash = () =>
            setRoute(window.location.hash.startsWith("#/admin") ? "admin" : "mail");
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, []);

    if (route === "admin") {
        return <AdminApp />;
    }

    return <MailFlow />;
}

function MailFlow() {
    const [phase, setPhase] = useState<Phase>("booting");
    const [bootError, setBootError] = useState("");
    const [loginData, setLoginData] = useState<LoginData | null>(null);

    // Boot — fetch AppData so the CSRF token is primed
    useEffect(() => {
        appData()
            .then(() => setPhase("login"))
            .catch((err) => setBootError(err instanceof Error ? err.message : "Failed to boot"));
    }, []);

    if (phase === "booting") {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0b0b0f] text-white">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-orange-500/20 bg-orange-500/10 text-2xl text-orange-400 shadow-[0_0_30px_rgba(249,115,22,0.2)]">
                    ✦
                </div>
                <Spinner size={24} label={bootError ? "Boot failed" : "Connecting…"} />
                {bootError && <p className="max-w-sm text-center text-sm text-red-400">{bootError}</p>}
            </div>
        );
    }

    if (phase === "login" || !loginData) {
        return (
            <LoginScreen
                onLoggedIn={(data) => {
                    setLoginData(data);
                    setPhase("mail");
                }}
            />
        );
    }

    return (
        <MailApp
            loginData={loginData}
            onLogout={() => {
                setLoginData(null);
                setPhase("login");
            }}
        />
    );
}

/** Navigate to the admin panel (`#/admin`). */
export function goAdmin() {
    window.location.hash = "#/admin";
}
